// Customer-display payload contract (protocol v1). Must stay in sync with the
// standalone mini-app copy at customer-display/src/customer-display.types.ts
// and the -pos original it was ported from.
export const CUSTOMER_DISPLAY_PROTOCOL_VERSION = 1 as const;

export type CustomerDisplayMode = 'idle' | 'order';

/**
 * Resolved color mode (no "system" here; the admin sends the effective mode).
 */
export type CustomerDisplayColorMode = 'light' | 'dark';

export interface ICustomerDisplayBranding {
  /** Store/restaurant name (always shown). */
  storeName: string;
  /** Store logo URL (optional). May be a remote URL or a data: URL. */
  logoUrl?: string | null;
  /** Small platform attribution, e.g. "queresto.com". */
  platformLabel: string;
}

export interface ICustomerDisplayOrderLine {
  qty: number;
  name: string;
  /**
   * Optional compact subtitle (e.g. selected options).
   * The customer display UI will render this as a single truncated line.
   */
  subtitle?: string;
  /** Pre-formatted currency (e.g. "$ 1.234,00"). */
  lineTotalLabel: string;
}

export interface ICustomerDisplayOrderSummary {
  lines: ICustomerDisplayOrderLine[];
  /**
   * If present and > 0, indicates there are additional items not shown in
   * `lines` due to UI space constraints.
   */
  hiddenLinesCount?: number;
  /** Pre-formatted currency (e.g. "$ 12.345,00"). */
  totalLabel: string;
}

export interface ICustomerDisplayPayloadV1 {
  protocolVersion: typeof CUSTOMER_DISPLAY_PROTOCOL_VERSION;
  branding: ICustomerDisplayBranding;
  mode: CustomerDisplayMode;
  /**
   * Optional for backward compatibility. If omitted, the customer display
   * falls back to its own preference (e.g. system).
   */
  colorMode?: CustomerDisplayColorMode;
  order?: ICustomerDisplayOrderSummary;
}

export type ICustomerDisplayPayload = ICustomerDisplayPayloadV1;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isCustomerDisplayPayloadV1(
  value: unknown
): value is ICustomerDisplayPayloadV1 {
  if (!isRecord(value)) return false;
  if (value.protocolVersion !== CUSTOMER_DISPLAY_PROTOCOL_VERSION) return false;
  if (value.mode !== 'idle' && value.mode !== 'order') return false;

  const colorMode = value.colorMode;
  if (
    !(colorMode === undefined || colorMode === 'light' || colorMode === 'dark')
  )
    return false;

  const branding = value.branding;
  if (!isRecord(branding)) return false;
  if (typeof branding.storeName !== 'string') return false;
  if (typeof branding.platformLabel !== 'string') return false;

  const logoUrl = branding.logoUrl;
  if (
    !(logoUrl === undefined || logoUrl === null || typeof logoUrl === 'string')
  )
    return false;

  const order = value.order;
  if (order === undefined) return true;
  if (!isRecord(order)) return false;
  const hiddenLinesCount = order.hiddenLinesCount;
  if (
    !(
      hiddenLinesCount === undefined ||
      (typeof hiddenLinesCount === 'number' &&
        Number.isInteger(hiddenLinesCount) &&
        hiddenLinesCount >= 0)
    )
  )
    return false;
  if (typeof order.totalLabel !== 'string') return false;
  if (!Array.isArray(order.lines)) return false;
  for (const line of order.lines) {
    if (!isRecord(line)) return false;
    if (typeof line.qty !== 'number' || !Number.isFinite(line.qty))
      return false;
    if (typeof line.name !== 'string') return false;
    if (typeof line.lineTotalLabel !== 'string') return false;
    const subtitle = line.subtitle;
    if (!(subtitle === undefined || typeof subtitle === 'string')) return false;
  }
  return true;
}
